import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Layers, Plus, Trash2, Star } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { FeatureGate } from "@/components/feature-gate";
import { listMyLenses, createLens, deleteLens } from "@/lib/command-center.functions";
import { listMyWorkspaces } from "@/lib/workspaces.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/lenses")({
  component: LensesPage,
});

function LensesPage() {
  return (
    <FeatureGate feature="custom_lenses" showBlurred={false}>
      <LensesInner />
    </FeatureGate>
  );
}

export function LensesInner() {
  const qc = useQueryClient();
  const fetchLenses = useServerFn(listMyLenses);
  const fetchWs = useServerFn(listMyWorkspaces);
  const create = useServerFn(createLens);
  const del = useServerFn(deleteLens);

  const { data: lensesData } = useQuery({ queryKey: ["my-lenses"], queryFn: () => fetchLenses() });
  const { data: wsData } = useQuery({ queryKey: ["my-workspaces"], queryFn: () => fetchWs() });

  const lenses = lensesData?.lenses ?? [];
  const workspaces = wsData?.workspaces ?? [];

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [isDefault, setIsDefault] = useState(false);

  const reset = () => { setName(""); setSelected([]); setIsDefault(false); };

  const handleCreate = async () => {
    if (!name.trim() || selected.length === 0) {
      toast.error("Name and at least one workspace required");
      return;
    }
    try {
      await create({ data: { name: name.trim(), workspace_ids: selected, is_default: isDefault, filter_config_json: {} } });
      toast.success("Lens created");
      setOpen(false);
      reset();
      qc.invalidateQueries({ queryKey: ["my-lenses"] });
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this lens?")) return;
    await del({ data: { lens_id: id } });
    toast.success("Lens deleted");
    qc.invalidateQueries({ queryKey: ["my-lenses"] });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Layers className="h-7 w-7 text-primary" /> Custom Lenses
          </h1>
          <p className="text-muted-foreground mt-1">
            Slice your Command Center by workspace groups, "Recurring only," "Q4 launches," anything.
          </p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
          <DialogTrigger asChild>
            <Button><Plus className="h-4 w-4 mr-1" /> New Lens</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create lens</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Recurring Programs" />
              </div>
              <div>
                <Label>Workspaces ({selected.length} selected)</Label>
                <div className="mt-2 max-h-64 overflow-y-auto border rounded-md p-2 space-y-1">
                  {workspaces.map((w: any) => (
                    <label key={w.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-muted/50 cursor-pointer">
                      <Checkbox
                        checked={selected.includes(w.id)}
                        onCheckedChange={(c) => setSelected((s) => c ? [...s, w.id] : s.filter((x) => x !== w.id))}
                      />
                      <span className="text-sm">{w.program_name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={isDefault} onCheckedChange={(c) => setIsDefault(!!c)} />
                Set as default lens
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={handleCreate}>Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {lenses.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No lenses yet. Create one to filter your Command Center by program group.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {lenses.map((l: any) => {
            const names = (l.workspace_ids ?? [])
              .map((id: string) => workspaces.find((w: any) => w.id === id)?.program_name)
              .filter(Boolean);
            return (
              <Card key={l.id}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 truncate">
                      {l.is_default && <Star className="h-3.5 w-3.5 fill-primary text-primary shrink-0" />}
                      {l.name}
                    </span>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(l.id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1">
                    {names.slice(0, 4).map((n: string, i: number) => (
                      <Badge key={i} variant="secondary" className="text-xs">{n}</Badge>
                    ))}
                    {names.length > 4 && <Badge variant="outline" className="text-xs">+{names.length - 4}</Badge>}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
